// ============================================================================
// OBT — Rol panellerinin ortak çerçevesi
// ============================================================================
// Roller arasındaki fark VERİ ve GÖREV ALANLARIDIR, düzen değil. Bu yüzden
// düzen tek yerde tutulur; her rol paneli yalnız kendi yapılandırmasını verir.
// Böylece ne tek devasa koşullu bileşen oluşur ne de düzen kopyalanır.
// ============================================================================

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import {
  ObtPageHeader,
  ObtLoading,
  ObtErrorState,
  ObtEmptyState,
  ObtStatTile,
  ObtStatusBadge,
  ObtTaskCard,
} from './ObtStates';
import { campaignPhase } from './obtEnums';

function formatDateTime(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CampaignRow({ campaign, onOpen }) {
  const due = formatDateTime(campaign?.response_due_at);
  const created = formatDateTime(campaign?.created_at);
  const clickable = typeof onOpen === 'function';
  return (
    <li
      onClick={clickable ? () => onOpen(campaign) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(campaign);
              }
            }
          : undefined
      }
      className={`rounded-lg border border-gray-200 bg-white p-3.5 transition ${
        clickable
          ? 'cursor-pointer hover:border-blue-300 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400'
          : ''
      }`}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">
            {campaign?.title || 'Başlıksız bilgi talebi'}
          </p>
          {/* Meta bilgiler ayrı satırda, etiketli — kartlar birbirine karışmasın */}
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500">
            {campaign?.topic && (
              <span>
                Konu:{' '}
                <span className="font-medium text-gray-700">{campaign.topic}</span>
              </span>
            )}
            {created && <span>Oluşturma: {created}</span>}
            {due && <span>Son cevap: {due}</span>}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <ObtStatusBadge {...campaignPhase(campaign)} />
        </div>
      </div>
    </li>
  );
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.description
 * @param {Array<{key: string, label: string, value: number, icon?: any, tone?: string}>} props.stats
 * @param {Array<{key: string, title: string, description: string, icon?: any, phase?: string}>} props.taskCards
 * @param {string} props.campaignsTitle
 * @param {string} props.campaignsEmptyText
 */
export default function ObtDashboardFrame({
  title,
  description,
  stats = [],
  taskCards = [],
  campaignsTitle = 'Bilgi Talepleri',
  campaignsEmptyText = 'Henüz kayıt bulunmuyor',
  campaigns = [],
  loading = false,
  error = null,
  onReload = null,
  onCampaignClick = null,
}) {
  return (
    <div className="space-y-6">
      <ObtPageHeader
        title={title}
        description={description}
        actions={
          onReload ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onReload}
              disabled={loading}
            >
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
              Yenile
            </Button>
          ) : null
        }
      />

      {/* Özet sayaçlar — yalnız gerçek backend verisi */}
      {loading && <ObtLoading label="OBT özeti yükleniyor…" />}

      {!loading && error && <ObtErrorState error={error} onRetry={onReload} />}

      {!loading && !error && stats.length > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {stats.map((stat) => (
            <ObtStatTile
              key={stat.key}
              label={stat.label}
              value={stat.value}
              icon={stat.icon}
              tone={stat.tone}
              onClick={stat.onClick}
            />
          ))}
        </div>
      )}

      {/* Kampanya listesi */}
      {!loading && !error && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{campaignsTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <ObtEmptyState
                title={campaignsEmptyText}
                description="Yeni bir bilgi talebi yayınlandığında burada görünecektir."
              />
            ) : (
              <ul className="space-y-2.5">
                {campaigns.map((campaign) => (
                  <CampaignRow
                    key={campaign.campaign_id || campaign.title}
                    campaign={campaign}
                    onOpen={onCampaignClick}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Görev alanları — tamamlanmamış işlevler durum etiketiyle işaretlenir */}
      {taskCards.length > 0 && (
        <section aria-labelledby="obt-task-areas">
          <h3
            id="obt-task-areas"
            className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500"
          >
            Görev Alanları
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {taskCards.map((card) => (
              <ObtTaskCard
                key={card.key}
                title={card.title}
                description={card.description}
                icon={card.icon}
                phase={card.phase}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
